import React, { useMemo } from "react";
import { Link, SlideoutDrawerV2, Text, QueryLoader } from "@jsq/ds/core";
import { useGetAccountingPayableForSlideoutQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { slideoutContainer, slideoutBody } from "../DistributionSlideout/styles_vanilla.css";
import { PaymentsContextProvider } from "../PaymentsListWorkflow/Context/PaymentsContext";
import { PaymentDetails } from "../PaymentsListWorkflow/PaymentDetails";
import { PaymentBatchesTable } from "./PaymentBatchesTable";
import { subtitleLink, subtitleText } from "./styles_vanilla.css";
import { TransactionDetailsCard } from "./TransactionDetailsCard";
import { AccountingPayableSlideoutProps } from "./types";
import { transformQueryResultToPayable } from "./utils";

export const AccountingPayableSlideout: React.FC<AccountingPayableSlideoutProps> = ({
  isOpen,
  onClose,
  id,
  onCreatePayment,
  onReviewClick,
  selectedPaymentBatchId,
  onReviewSlideoutClose,
  paymentsContext,
}) => {
  const queryResult = useGetAccountingPayableForSlideoutQuery({
    variables: { id },
    context: clientContext,
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const payable = useMemo(
    () => transformQueryResultToPayable(queryResult.data),
    [queryResult.data],
  );

  const handleCreatePayment = () => {
    if (payable && onCreatePayment) {
      onCreatePayment(payable);
    }
  };

  const slideoutContent = (
    <SlideoutDrawerV2
      // @ts-expect-error - OverlayStyle enum properly exported but auto-formatter removes import
      overlayStyle="transparent"
      isOpen={isOpen}
      onClose={onClose}
      data-testid="accounting-payable-slideout"
    >
      <div className={slideoutContainer}>
        <QueryLoader result={queryResult}>
          <SlideoutDrawerV2.ComplexHeader
            title={payable?.entityName ?? ""}
            subtitles={[
              payable?.transactionUrl ? (
                <Text key="subtitle" className={subtitleText}>
                  {payable.label}
                  {payable.subtitleLabel ? ` (${payable.subtitleLabel})` : ""}
                  {" · "}
                  <Link
                    href={payable.transactionUrl}
                    className={subtitleLink}
                    icon="external-link"
                    iconPosition="right"
                    shouldOpenInNewTab
                  >
                    Transaction details
                  </Link>
                </Text>
              ) : (
                `${payable?.label ?? ""}${payable?.subtitleLabel ? ` (${payable.subtitleLabel})` : ""}`
              ),
            ]}
          />
          <div className={slideoutBody}>
            {payable && (
              <TransactionDetailsCard
                payable={payable}
                entityUrl={payable.entityUrl}
                onCreatePayment={onCreatePayment ? handleCreatePayment : undefined}
              />
            )}
            <PaymentBatchesTable
              paymentBatches={queryResult.data?.accountingPayable?.paymentBatches ?? []}
              loading={queryResult.loading}
              onReviewClick={onReviewClick}
            />
          </div>
        </QueryLoader>
      </div>
    </SlideoutDrawerV2>
  );

  const reviewSlideoutContent =
    paymentsContext && selectedPaymentBatchId ? (
      <PaymentDetails
        paymentBatchId={selectedPaymentBatchId}
        onClose={onReviewSlideoutClose ?? (() => {})}
        totalPositions={queryResult.data?.accountingPayable?.payeeCount}
      />
    ) : null;

  if (paymentsContext) {
    return (
      <PaymentsContextProvider value={paymentsContext}>
        {slideoutContent}
        {reviewSlideoutContent}
      </PaymentsContextProvider>
    );
  }

  return slideoutContent;
};
